import * as React from 'react';
import { debounce } from 'es-toolkit';

const DEFAULT_INPUT_DEBOUNCE_MS = 200;

export interface DebouncedInputValue {
  /** Value to render in the input — tracks keystrokes immediately. */
  inputValue: string;
  /** Keystroke handler — updates `inputValue` and schedules the debounced `onChange`. */
  handleChange: (next: string) => void;
  /** Emit any pending edit immediately (call on blur, before commit logic). */
  flush: () => void;
}

/**
 * Debounces a controlled input's `onChange` while keeping keystrokes visible:
 * the returned `inputValue` updates immediately, the upstream `onChange` fires
 * after `delayMs` of typing inactivity, on `flush()` (blur), or on unmount.
 * While an edit is pending, external `value` changes are ignored — the user's
 * in-progress text wins. Unlike `useDebounce` (a plain trailing value), this
 * exposes the synchronous flush that commit-on-blur flows require.
 */
export function useDebouncedInputValue(
  value: string,
  onChange: (value: string) => void,
  delayMs: number = DEFAULT_INPUT_DEBOUNCE_MS
): DebouncedInputValue {
  const [inputValue, setInputValue] = React.useState(value);
  const pendingRef = React.useRef<string | null>(null);
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;

  // Sync from the controlled prop unless the user has an unflushed edit.
  React.useEffect(() => {
    if (pendingRef.current === null) setInputValue(value);
  }, [value]);

  const emitPending = React.useMemo(
    () =>
      debounce(() => {
        if (pendingRef.current === null) return;
        const pending = pendingRef.current;
        pendingRef.current = null;
        onChangeRef.current(pending);
      }, delayMs),
    [delayMs]
  );

  const handleChange = React.useCallback(
    (next: string) => {
      setInputValue(next);
      pendingRef.current = next;
      emitPending();
    },
    [emitPending]
  );

  const flush = React.useCallback(() => emitPending.flush(), [emitPending]);

  // Flush a pending edit when the field unmounts (or delayMs changes).
  React.useEffect(() => () => emitPending.flush(), [emitPending]);

  return { inputValue, handleChange, flush };
}
